import * as DropdownMenuPrimitive from '@rn-primitives/dropdown-menu'
import type React from 'react'
import {
  Platform,
  StyleSheet,
  // eslint-disable-next-line @typescript-eslint/no-restricted-imports
  Text,
  View,
  type StyleProp,
  type TextProps,
  type ViewStyle,
} from 'react-native'
import { cn } from '~/lib/utils'
import Icon from '../Icon'

const DropdownMenu = DropdownMenuPrimitive.Root

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger

const DropdownMenuGroup = DropdownMenuPrimitive.Group

const DropdownMenuPortal = DropdownMenuPrimitive.Portal

const DropdownMenuSub = DropdownMenuPrimitive.Sub

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup

function DropdownMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}: DropdownMenuPrimitive.SubTriggerProps & {
  ref?: React.RefObject<DropdownMenuPrimitive.SubTriggerRef>
  className?: string
  inset?: boolean
  children?: React.ReactNode
}) {
  const { open } = DropdownMenuPrimitive.useSubContext()
  const textClass = cn(
    'native:text-lg select-none text-sm text-primary',
    open && 'native:text-accent-foreground'
  )

  return (
    <DropdownMenuPrimitive.SubTrigger
      className={cn(
        'native:py-2 web:cursor-default web:select-none web:outline-none web:hover:bg-accent web:focus:bg-accent flex flex-row items-center gap-2 rounded-md px-2 py-1.5 active:bg-accent',
        open && 'bg-accent',
        inset && 'pl-8',
        className
      )}
      {...props}>
      {typeof children === 'string' ? <Text className={textClass}>{children}</Text> : children}
      <Icon
        name={open ? 'chevron-up' : 'chevron-down'}
        size={18}
        className="ml-auto text-foreground"
      />
    </DropdownMenuPrimitive.SubTrigger>
  )
}

function DropdownMenuSubContent({
  className,
  ...props
}: DropdownMenuPrimitive.SubContentProps & {
  ref?: React.RefObject<DropdownMenuPrimitive.SubContentRef>
}) {
  const { open } = DropdownMenuPrimitive.useSubContext()
  return (
    <DropdownMenuPrimitive.SubContent
      className={cn(
        'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 mt-1 min-w-32 overflow-hidden rounded-md border border-border bg-popover p-1 shadow-md shadow-foreground/5',
        open
          ? 'web:animate-in web:fade-in-0 web:zoom-in-95'
          : 'web:animate-out web:fade-out-0 web:zoom-out',
        className
      )}
      {...props}
    />
  )
}

function DropdownMenuContent({
  className,
  overlayClassName,
  overlayStyle,
  portalHost,
  ...props
}: DropdownMenuPrimitive.ContentProps & {
  ref?: React.RefObject<DropdownMenuPrimitive.ContentRef>
  overlayStyle?: StyleProp<ViewStyle>
  overlayClassName?: string
  portalHost?: string
}) {
  const { open } = DropdownMenuPrimitive.useRootContext()
  return (
    <DropdownMenuPrimitive.Portal hostName={portalHost}>
      <DropdownMenuPrimitive.Overlay
        style={
          overlayStyle
            ? StyleSheet.flatten([
                Platform.OS !== 'web' ? StyleSheet.absoluteFill : undefined,
                overlayStyle as typeof StyleSheet.absoluteFill,
              ])
            : Platform.OS !== 'web'
              ? StyleSheet.absoluteFill
              : undefined
        }
        className={overlayClassName}>
        <DropdownMenuPrimitive.Content
          className={cn(
            'web:data-[side=bottom]:slide-in-from-top-2 web:data-[side=left]:slide-in-from-right-2 web:data-[side=right]:slide-in-from-left-2 web:data-[side=top]:slide-in-from-bottom-2 z-50 min-w-32 overflow-hidden rounded-md border border-border bg-popover px-2 py-2 shadow-md shadow-foreground/5',
            open
              ? 'web:animate-in web:fade-in-0 web:zoom-in-95'
              : 'web:animate-out web:fade-out-0 web:zoom-out-95',
            className
          )}
          {...props}
        />
      </DropdownMenuPrimitive.Overlay>
    </DropdownMenuPrimitive.Portal>
  )
}

function DropdownMenuItem({
  className,
  inset,
  children,
  ...props
}: DropdownMenuPrimitive.ItemProps & {
  ref?: React.RefObject<DropdownMenuPrimitive.ItemRef>
  className?: string
  inset?: boolean
}) {
  const textClass = cn(
    'native:text-lg web:group-focus:text-accent-foreground select-none text-sm text-popover-foreground'
  )

  return (
    <DropdownMenuPrimitive.Item
      className={cn(
        'group/button native:py-2 web:cursor-default web:cursor-pointer web:outline-none web:hover:bg-accent web:focus:bg-accent relative flex flex-row items-center gap-2 rounded-md px-4 py-3 active:bg-accent',
        inset && 'pl-8',
        props.disabled && 'web:pointer-events-none opacity-50',
        className
      )}
      {...props}>
      {typeof children === 'string' ? <Text className={textClass}>{children}</Text> : children}
    </DropdownMenuPrimitive.Item>
  )
}

function DropdownMenuCheckboxItem({
  className,
  children,
  checked,
  ...props
}: DropdownMenuPrimitive.CheckboxItemProps & {
  ref?: React.RefObject<DropdownMenuPrimitive.CheckboxItemRef>
  children?: React.ReactNode
}) {
  return (
    <DropdownMenuPrimitive.CheckboxItem
      className={cn(
        'web:group native:py-2 web:cursor-default web:outline-none web:focus:bg-accent relative flex flex-row items-center rounded-md py-1.5 pl-8 pr-2 active:bg-accent',
        props.disabled && 'web:pointer-events-none opacity-50',
        className
      )}
      checked={checked}
      {...props}>
      <View className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <Icon name="checkmark" size={14} className="text-foreground" />
        </DropdownMenuPrimitive.ItemIndicator>
      </View>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  )
}

function DropdownMenuRadioItem({
  className,
  children,
  ...props
}: DropdownMenuPrimitive.RadioItemProps & {
  ref?: React.RefObject<DropdownMenuPrimitive.RadioItemRef>
  children?: React.ReactNode
}) {
  return (
    <DropdownMenuPrimitive.RadioItem
      className={cn(
        'web:group native:py-2 web:cursor-default web:outline-none web:focus:bg-accent relative flex flex-row items-center rounded-md py-1.5 pl-8 pr-2 active:bg-accent',
        props.disabled && 'web:pointer-events-none opacity-50',
        className
      )}
      {...props}>
      <View className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <View className="h-2 w-2 rounded-full bg-foreground" />
        </DropdownMenuPrimitive.ItemIndicator>
      </View>
      {children}
    </DropdownMenuPrimitive.RadioItem>
  )
}

function DropdownMenuLabel({
  className,
  inset,
  ...props
}: DropdownMenuPrimitive.LabelProps & {
  ref?: React.RefObject<DropdownMenuPrimitive.LabelRef>
  className?: string
  inset?: boolean
}) {
  return (
    <DropdownMenuPrimitive.Label
      className={cn(
        'native:text-base web:cursor-default px-2 py-1.5 font-semibold text-sm text-foreground',
        inset && 'pl-8',
        className
      )}
      {...props}
    />
  )
}

function DropdownMenuSeparator({
  className,
  ...props
}: DropdownMenuPrimitive.SeparatorProps & {
  ref?: React.RefObject<DropdownMenuPrimitive.SeparatorRef>
}) {
  return (
    <DropdownMenuPrimitive.Separator
      className={cn('-mx-1 my-1 h-px bg-border', className)}
      {...props}
    />
  )
}

function DropdownMenuShortcut({ className, ...props }: TextProps) {
  return (
    <Text
      className={cn('native:text-sm ml-auto text-xs tracking-widest text-foreground', className)}
      {...props}
    />
  )
}

export {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
}
